import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Tooltip.css';

interface TooltipProps {
	content?: React.ReactNode;
	children: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({ content, children }) => {
	const [isOpen, setIsOpen] = useState(false);
	const tooltipRef = useRef<HTMLDivElement | null>(null);
	const rafRef = useRef<number | null>(null);
	const nextPositionRef = useRef({ x: 0, y: 0 });

	const applyPosition = useCallback(() => {
		rafRef.current = null;
		const tooltipElement = tooltipRef.current;
		if (!tooltipElement) {
			return;
		}

		tooltipElement.style.left = `${nextPositionRef.current.x + 12}px`;
		tooltipElement.style.top = `${nextPositionRef.current.y + 16}px`;
	}, []);

	const updatePosition = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		const rect = event.currentTarget.getBoundingClientRect();
		nextPositionRef.current = {
			x: event.clientX - rect.left,
			y: event.clientY - rect.top,
		};

		if (rafRef.current === null) {
			rafRef.current = requestAnimationFrame(applyPosition);
		}
	}, [applyPosition]);

	useEffect(() => {
		return () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
			}
		};
	}, []);

	if (!content) {
		return <>{children}</>;
	}

	return (
		<div
			className="custom-tooltip"
			onMouseEnter={(event) => {
				setIsOpen(true);
				updatePosition(event);
			}}
			onMouseMove={updatePosition}
			onMouseLeave={() => setIsOpen(false)}
			onFocusCapture={() => setIsOpen(true)}
			onBlurCapture={() => setIsOpen(false)}
		>
			{children}
			{isOpen && (
				<div
					ref={tooltipRef}
					role="tooltip"
					className="custom-tooltip__content"
					data-testid="custom-tooltip-content"
					style={{
						left: `${nextPositionRef.current.x + 12}px`,
						top: `${nextPositionRef.current.y + 16}px`,
					}}
				>
					{content}
				</div>
			)}
		</div>
	);
};

export default Tooltip;